import React, { useMemo } from 'react';

import { FlexColumn } from 'ui-components/components/flex';
import { useMaxZ } from 'ui-components/hooks/services/use-max-z';

import { useCustomizeLayoutContext, SECTION_IDS } from '../../custom-layout/customize-layout-provider';
import { FraudIndicatorsSection } from '../../fraud-indicators/fraud-indicators-section';

import style from './selected-content-grid-layout.module.less';

/**
 * @param {object} props
 * @param {string} [props.className]
 * @param {React.ReactNode} [props.column1Sections]
 * @param {React.ReactNode} [props.column2Sections]
 * @param {React.ReactNode} [props.column3Sections]
 * @param {React.ReactNode} props.fulfillmentPlannerSections
 * @param {React.ReactNode} props.primaryShipmentSections
 * @param {React.ReactNode} [props.rateCard]
 * @param {React.ReactNode} props.secondaryShipmentSections
 * @returns {JSX.Element}
 */
export const SelectedContentGridLayout = ({
  className,
  column1Sections,
  column2Sections,
  column3Sections,
  primaryShipmentSections,
  fulfillmentPlannerSections,
  secondaryShipmentSections,
  rateCard = null,
}) => {
  const zIndex = useMaxZ();
  const zIndexStyle = useMemo(() => ({ zIndex }), [zIndex]);
  const { layoutMode, shouldUseCustomLayout, column1Order, column2Order, column3Order } = useCustomizeLayoutContext();

  const hasCustomColumns = column1Sections || column2Sections || column3Sections;
  const _shouldUseCustomLayout = shouldUseCustomLayout && hasCustomColumns;

  // Check if fraud indicators are in any column of the custom layout
  const fraudIndicatorsInLayout =
    column1Order.includes(SECTION_IDS.fraudIndicators) ||
    column2Order.includes(SECTION_IDS.fraudIndicators) ||
    column3Order.includes(SECTION_IDS.fraudIndicators);

  // Only render fraud indicators outside the layout if using custom layout AND they're not already in it
  const shouldRenderFraudIndicatorsOutside = _shouldUseCustomLayout && !fraudIndicatorsInLayout;

  const gridProps = _shouldUseCustomLayout ? { 'data-layout-mode': layoutMode } : {};

  return (
    <div className={className} {...gridProps}>
      {shouldRenderFraudIndicatorsOutside && <FraudIndicatorsSection />}
      {_shouldUseCustomLayout ? (
        <>
          <FlexColumn className={style.column1}>{column1Sections}</FlexColumn>
          <FlexColumn className={style.column2}>{column2Sections}</FlexColumn>
          {layoutMode === 3 && <FlexColumn className={style.column3}>{column3Sections}</FlexColumn>}
        </>
      ) : (
        <>
          <FlexColumn className={style['primary-shipment-sections']}>{primaryShipmentSections}</FlexColumn>
          <FlexColumn className={style['fulfillment-planner-sections']}>{fulfillmentPlannerSections}</FlexColumn>
          <FlexColumn className={style['secondary-shipment-sections']}>{secondaryShipmentSections}</FlexColumn>
        </>
      )}
      {!!rateCard && (
        <div className={style['rate-card']} style={zIndexStyle}>
          {rateCard}
        </div>
      )}
    </div>
  );
};
